import LoginForm from '@pages/login/components/login/LoginForm/indext';
import { ForgotPasswordForm } from '@pages/login/components/login/ForgotPasswordForm';
import ResetPasswordForm from '@pages/login/components/login/ResetPasswordForm';
import ForceResetPasswordForm from '@pages/login/components/login/ForceResetPasswordForm';
import { useFormik } from 'formik';
import { useState, useEffect, useMemo } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Loading } from '@components/atoms/Loading';
import useLogin from '@hooks/api/auth/useLogin';
import useForgotPassword from '@hooks/api/auth/useForgotPassword';
import { Header } from './components/Header';
import {
  forgotPasswordSchema,
  loginSchema,
  resetPasswordSchema,
  forceResetPasswordSchema,
} from './schemaValidations';
import {
  setResetPasswordEmail,
  getResetPasswordEmail,
  removeResetPasswordEmail,
} from '@config/storage';
import { useTranslation } from '@hooks/useTranslation';

const LoginPage: React.FC = () => {
  // Sprint 28, story-143. Bucket 1 (pre-auth) — device/browser auto-detect, per
  // docs/architecture.md "## Sprint 28" Decision 4. `useTranslation` already reads the correct
  // source on this surface; no new detection call is added here.
  const tLogin = useTranslation('login');
  const tPolicy = useTranslation('passwordPolicy');
  const location = useLocation();
  const history = useHistory();
  const [showReset, setShowReset] = useState<boolean>(false);
  const [showForgotPassword, setShowForgotPassword] = useState<boolean>(false);
  const [forceResetPassword, setForceResetPassword] = useState<boolean>(false);

  // Check for code-only deeplink on mount to get initial values
  const getInitialResetValues = () => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const user = params.get('user');

    // If code is present but NO user param, this is a password reset deeplink
    if (code && !user) {
      const storedEmail = getResetPasswordEmail();
      return {
        email: storedEmail || '',
        newPassword: '',
        confirmPassword: '',
        confirmationCode: code,
      };
    }
    return {
      email: '',
      newPassword: '',
      confirmPassword: '',
      confirmationCode: '',
    };
  };

  const { signIn, confirmInitialSignIn, isLoading } = useLogin();

  const loginValidationSchema = useMemo(() => loginSchema(tLogin), [tLogin]);
  const resetPasswordValidationSchema = useMemo(() => resetPasswordSchema(tLogin, tPolicy), [tLogin, tPolicy]);
  const forgotPasswordValidationSchema = useMemo(() => forgotPasswordSchema(tLogin), [tLogin]);
  const forceResetPasswordValidationSchema = useMemo(
    () => forceResetPasswordSchema(tLogin, tPolicy),
    [tLogin, tPolicy],
  );

  const {
    forgotPasswordMutation,
    confirmResetMutation,
    isLoading: isForgotLoading,
  } = useForgotPassword();

  const handleBack = () => {
    setShowReset(false);
    setShowForgotPassword(false);
    setForceResetPassword(false);
    // Remove query parameters when going back
    if (location.search) {
      history.push('/login');
    }
  };

  // A deeplink from an invitation email carries both "user" and "code". The
  // code is a one-time temporary password, so the link is dead once the user
  // has set a real password.
  const isDeeplinkLogin = () =>
    /[?&]user=/.test(location.search) && /[?&]code=/.test(location.search);

  const loginFormik = useFormik({
    validationSchema: loginValidationSchema,
    initialValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
    onSubmit: async (values, { setFieldValue }) => {
      const fromDeeplink = isDeeplinkLogin();
      try {
        const res = await signIn({
          user: values.email,
          password: values.password,
          rememberMe: values.rememberMe,
        });

        // A spent deeplink can't recover, so strip it from the URL and drop the
        // user on a clean login page instead of leaving the dead code in the
        // address bar to be retried on the next render or reload.
        if (res && res.status === 'FAILURE' && fromDeeplink) {
          setFieldValue('password', '');
          toast.info(tLogin.deeplinkUsed);
          history.replace('/login');
          return;
        }

        if (
          res &&
          res.status === 'REQUIRES_PASSWORD_CHANGE' &&
          'confirmToken' in res
        ) {
          setForceResetPassword(true);
          forceResetPasswordFormik.setFieldValue('email', values.email);
          forceResetPasswordFormik.setFieldValue(
            'confirmToken',
            res.confirmToken,
          );
          forceResetPasswordFormik.setFieldValue(
            'rememberMe',
            values.rememberMe,
          );
        }
      } catch (error) {
        console.error('Login error:', error);
      }
    },
  });

  const resetPasswordFormik = useFormik({
    validationSchema: resetPasswordValidationSchema,
    initialValues: getInitialResetValues(),
    enableReinitialize: true,
    onSubmit: async (values) => {
      const result: any = await confirmResetMutation.mutateAsync({
        email: values.email,
        confirmationCode: values.confirmationCode,
        newPassword: values.newPassword,
      });

      if (result?.confirmation === '200') {
        // Clear stored email after successful password reset
        removeResetPasswordEmail();
        await signIn({
          user: values.email,
          password: values.newPassword,
          rememberMe: loginFormik.values.rememberMe,
        });
        setShowReset(false);
        setShowForgotPassword(false);
      }
    },
  });

  const forgotPasswordFormik = useFormik({
    validationSchema: forgotPasswordValidationSchema,
    initialValues: {
      email: '',
    },
    onSubmit: async (values) => {
      await forgotPasswordMutation.mutateAsync(values.email);
      // Store email for deeplink scenario (when user clicks link in email)
      setResetPasswordEmail(values.email);
      resetPasswordFormik.setFieldValue(
        'email',
        forgotPasswordFormik.values.email,
      );
      setShowForgotPassword(false);

      setShowReset(true);
    },
  });

  const forceResetPasswordFormik = useFormik({
    validationSchema: forceResetPasswordValidationSchema,
    initialValues: {
      email: '',
      confirmToken: '',
      rememberMe: false,
      newPassword: '',
      confirmPassword: '',
    } as any,
    onSubmit: async (values: any) => {
      await confirmInitialSignIn(
        values.email,
        values.newPassword,
        values.confirmToken,
        values.rememberMe,
      );
    },
  });

  const handleForgotPassword = () => {
    forgotPasswordFormik.setFieldValue('email', loginFormik.values.email);
    setShowForgotPassword(true);
  };

  const handleForgotPasswordSubmit = () => {
    forgotPasswordFormik.handleSubmit();
  };

  // Check URL params for forgot password action or password reset deeplinks
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const user = params.get('user');

    if (params.get('action') === 'forgot-password') {
      setShowForgotPassword(true);
    }
    // If code is present but NO user param, this is a password reset deeplink
    // (Initial login deeplinks have both code AND user params)
    // Initial values are already set via getInitialResetValues()
    else if (code && !user) {
      setShowReset(true);
    }
  }, [location.search]);

  if (isForgotLoading || isLoading) {
    return <Loading size="2xl" center />;
  }

  return (
    <div className="bg-white dark:bg-gray-900">
      <Header />
      {showReset && (
        <ResetPasswordForm onBack={handleBack} formik={resetPasswordFormik} />
      )}
      {forceResetPassword && (
        <ForceResetPasswordForm
          onBack={handleBack}
          formik={forceResetPasswordFormik}
        />
      )}
      {showForgotPassword && (
        <ForgotPasswordForm
          onBack={handleBack}
          formik={forgotPasswordFormik}
          submitting={forgotPasswordFormik.isSubmitting}
          handleSubmit={handleForgotPasswordSubmit}
        />
      )}
      {!showReset && !showForgotPassword && !forceResetPassword && (
        <LoginForm
          formik={loginFormik}
          onForgotPassword={handleForgotPassword}
        />
      )}
    </div>
  );
};

export default LoginPage;
